import { createSlice } from "@reduxjs/toolkit";
import { RootState } from "../../store";
import type {
  LinkRecord,
  Tag,
  Collection,
  CollectionFavorite,
  Snippet,
  UserExtended,
  AIContext,
  RecentAnswer,
  EmployeeProfile,
  Workspace,
} from "winslow-core";
import type { TrackerData } from "../../../hooks/useTracker";

export const APP_MENUS = {
  EMPLOYEES: "employees",
  EMPLOYEE_NEW: "employee-new",
  FLATTEN: "flatten",
  DOCUMENTS: "documents",
  RESOURCES: "resources",
  ANSWERS: "answers",
  SETTINGS: "settings",
  CUSTOM_PROPERTIES: "custom-properties",
  UNSELECTED: "unselected",
  TIME_OFF: "time-off",
  EMPLOYEE_CALENDAR: "employee-calendar",
};

export type uiStatesType = {
  searchOpen: boolean;
  folderPickerOpen: boolean;
  snippetSidebarOpen: boolean;
  snippetEdited: boolean;
  confirmDiscardSnippetOpen: boolean;
  confirmDeleteSnippetOpen: boolean;
  answerSidebarOpen: boolean;
  employeeProfileOpen: boolean;
  appSelectedMenu: string;
  resourceSidebarOpen: boolean;
};

export type AppDataState = {
  user: UserExtended | undefined;
  token: string | undefined;
  trackerData: TrackerData | undefined;
  signUpNow: boolean;
  selectedMenu: string | undefined;
  selectedSubMenu: string | undefined;
  selectedMenuId: string | undefined;
  collections: Collection[] | null;
  collectionFavorites: CollectionFavorite[] | null;
  linkRecords: LinkRecord[] | null;
  tags: Tag[] | null;
  memberAdded: boolean;
  uiStates: uiStatesType;
  aiContext: AIContext;
  isMobile: boolean;
  currentCollectionId: string | null;
  editingCollection: Collection | null;
  snippets: Snippet[] | null;
  sidebarSnippet: Snippet | null;
  sidebarAnswer: RecentAnswer | null;
  onDiscardConfirm: any;
  selectedEmployeeId: string | null;
  selectedPersonaId: string | null;
  mergeConnected: boolean;
  resourcesSearchText: string;
};

const initialState = {
  user: undefined,
  token: undefined,
  trackerData: undefined,
  signUpNow: false,
  selectedMenu: "unselected",
  selectedSubMenu: "unselected",
  selectedMenuId: undefined,
  collections: null,
  collectionFavorites: null,
  linkRecords: null,
  tags: null,
  memberAdded: false,
  uiStates: {
    searchOpen: false,
    folderPickerOpen: false,
    snippetSidebarOpen: false,
    snippetEdited: false,
    confirmDiscardSnippetOpen: false,
    confirmDeleteSnippetOpen: false,
    answerSidebarOpen: false,
    employeeProfileOpen: false,
    appSelectedMenu: APP_MENUS.EMPLOYEES,
    resourceSidebarOpen: false,
  },
  aiContext: {
    linkRecordId: undefined,
    message: "",
    prompts: undefined,
    readyToProcess: false,
  },
  isMobile: false,
  currentCollectionId: null,
  editingCollection: null,
  snippets: null,
  sidebarSnippet: null,
  sidebarAnswer: null,
  onDiscardConfirm: null,
  selectedEmployeeId: null,
  selectedPersonaId: null,
  mergeConnected: false,
  resourcesSearchText: "",
} as AppDataState;

export const appDataSlice = createSlice({
  name: "appData",
  initialState,
  reducers: {
    setUser: (state, action) => {
      state.user = action.payload;
      const workspace = action.payload?.workspaces?.find(
        (w: Workspace) => w.id === action.payload?.currentWorkspaceId
      );
      state.mergeConnected = workspace?.mergeAccountToken !== null;
    },
    clearUser: (state) => {
      state.user = undefined;
    },
    setToken: (state, action) => {
      state.token = action.payload;
    },
    clearToken: (state) => {
      state.token = undefined;
    },
    setTrackerData: (state, action) => {
      state.trackerData = action.payload;
    },
    setSignUpNow: (state, action) => {
      state.signUpNow = action.payload;
    },
    setSelectedMenu: (state, action) => {
      state.selectedMenu = action.payload;
      state.uiStates = {
        ...state.uiStates,
        answerSidebarOpen: false,
        snippetSidebarOpen: false,
      };
    },
    setSelectedSubMenu: (state, action) => {
      state.selectedSubMenu = action.payload;
    },
    setSelectedMenuId: (state, action) => {
      state.selectedMenuId = action.payload;
    },
    setCollections: (state, action) => {
      state.collections = action.payload;
    },
    setCollectionFavorites: (state, action) => {
      state.collectionFavorites = action.payload;
    },
    setLinkRecords: (state, action) => {
      state.linkRecords = action.payload;
    },
    setTags: (state, action) => {
      state.tags = action.payload;
    },
    setLinkRecordTitle: (state, action) => {
      if (state.linkRecords) {
        const lrIndex = state.linkRecords.findIndex((linkRecord: any) => {
          return linkRecord.id === action.payload.linkRecordId;
        });

        if (lrIndex !== -1) {
          const linkRecord = state.linkRecords[lrIndex];

          if (linkRecord && linkRecord.links && linkRecord.links.length > 0) {
            linkRecord.links[0].title = action.payload.title;
          }
        }
      }
    },
    setDeleteLinkRecord: (state, action) => {
      if (state.linkRecords) {
        const linkRecordIndex = state.linkRecords.findIndex(
          (linkRecord: any) => {
            return linkRecord.id === action.payload;
          }
        );

        if (linkRecordIndex !== -1) {
          state.linkRecords.splice(linkRecordIndex, 1);
        }
      }
    },
    // not only do we need to chnage the core linkRecord array
    // we need to change all references to that linkRecord in the shares
    // array too to keep them in sync
    updateLinkRecord: (state, action) => {
      if (state.linkRecords) {
        const index = state.linkRecords.findIndex((linkRecord: any) => {
          return linkRecord.id === action.payload.id;
        });

        if (index !== -1) {
          state.linkRecords[index] = { ...action.payload };
        }
      }
    },
    updateTagName: (state, action) => {
      if (state.tags) {
        const { tagId, name } = action.payload;

        const index = state.tags.findIndex((tag: any) => {
          return tag.id === tagId;
        });

        if (index !== -1) {
          state.tags[index].name = name;
        }
      }
    },
    setUIStates: (state, action) => {
      state.uiStates = { ...action.payload };
    },
    setAIContext: (state, action) => {
      state.aiContext = { ...action.payload };
    },
    setMemberAdded: (state, action) => {
      state.memberAdded = action.payload;
    },
    setIsMobile: (state, action) => {
      state.isMobile = action.payload;
    },
    setCurrentCollectionId: (state, action) => {
      state.currentCollectionId = action.payload;
    },
    setEditingCollection: (state, action) => {
      state.editingCollection = action.payload;
    },
    setSnippets: (state, action) => {
      state.snippets = action.payload;
    },
    setSidebarSnippet: (state, action) => {
      state.sidebarSnippet = action.payload;
    },
    setOnDiscardConfirm: (state, action) => {
      state.onDiscardConfirm = action.payload;
    },
    setSidebarAnswer: (state, action) => {
      state.sidebarAnswer = action.payload;
    },
    setSelectedEmployeeId: (state, action) => {
      state.selectedEmployeeId = action.payload;
    },
    setSelectedPersonaId: (state, action) => {
      state.selectedPersonaId = action.payload;
    },
    setMergeConnected: (state, action) => {
      state.mergeConnected = action.payload;
    },
    setResourcesSearchText: (state, action) => {
      state.resourcesSearchText = action.payload;
    },
  },
});

export const {
  setUser,
  clearUser,
  setToken,
  clearToken,
  setTrackerData,
  setSignUpNow,
  setSelectedMenu,
  setSelectedSubMenu,
  setSelectedMenuId,
  setCollections,
  setCollectionFavorites,
  setLinkRecords,
  setTags,
  setLinkRecordTitle,
  setDeleteLinkRecord,
  updateLinkRecord,
  updateTagName,
  setUIStates,
  setAIContext,
  setMemberAdded,
  setIsMobile,
  setCurrentCollectionId,
  setEditingCollection,
  setSnippets,
  setSidebarSnippet,
  setSidebarAnswer,
  setOnDiscardConfirm,
  setSelectedEmployeeId,
  setSelectedPersonaId,
  setMergeConnected,
  setResourcesSearchText,
} = appDataSlice.actions;

// Other code such as selectors can use the imported `RootState` type
export const selectUser = (state: RootState) => state.appData.user;
export const selectToken = (state: RootState) => state.appData.token;
export const selectTrackerData = (state: RootState) =>
  state.appData.trackerData;
export const selectSignupNow = (state: RootState) => state.appData.signUpNow;
export const selectSelectedMenu = (state: RootState) =>
  state.appData.selectedMenu;
export const selectSelectedSubMenu = (state: RootState) =>
  state.appData.selectedSubMenu;
export const selectSelectedMenuId = (state: RootState) =>
  state.appData.selectedMenuId;
export const selectCollections = (state: RootState) =>
  state.appData.collections;
export const selectCollectionFavorites = (state: RootState) =>
  state.appData.collectionFavorites;
export const selectLinkRecords = (state: RootState) =>
  state.appData.linkRecords;
export const selectTags = (state: RootState) => state.appData.tags;
export const selectUIStates = (state: RootState) => state.appData.uiStates;
export const selectAIContext = (state: RootState) => state.appData.aiContext;
export const selectMemberAdded = (state: RootState) =>
  state.appData.memberAdded;
export const selectIsMobile = (state: RootState) => state.appData.isMobile;
export const selectCurrentCollectionId = (state: RootState) =>
  state.appData.currentCollectionId;
export const selectEditingCollection = (state: RootState) =>
  state.appData.editingCollection;
export const selectSnippets = (state: RootState) => state.appData.snippets;
export const selectSidebarSnippet = (state: RootState) =>
  state.appData.sidebarSnippet;
export const selectSidebarAnswer = (state: RootState) =>
  state.appData.sidebarAnswer;
export const selectOnDiscardConfirm = (state: RootState) =>
  state.appData.onDiscardConfirm;
export default appDataSlice.reducer;
export const selectSelectedEmployeeId = (state: RootState) =>
  state.appData.selectedEmployeeId;
export const selectSelectedPersonaId = (state: RootState) =>
  state.appData.selectedPersonaId;
export const selectMergeConnected = (state: RootState) =>
  state.appData.mergeConnected;
export const selectResourcesSearchText = (state: RootState) =>
  state.appData.resourcesSearchText;
